import { CascadiaColor } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { mergeProps } from "react-aria";
import {
  Row as AriaRow,
  Button,
  Collection,
  RowProps,
  useTableOptions,
} from "react-aria-components";

import { Cell } from "./Cell";
import { TableCheckbox } from "./TableCheckbox";

interface ExtendedRowProps<T> extends RowProps<T> {
  backgroundColor?: CascadiaColor;
}

export function Row<T extends object>({
  id,
  columns,
  children,
  backgroundColor,
  ...otherProps
}: ExtendedRowProps<T>) {
  const { selectionBehavior, allowsDragging } = useTableOptions();

  return (
    <AriaRow
      id={id}
      {...mergeProps(otherProps, {
        className: css({
          ...styles.row,
          backgroundColor: backgroundColor && `var(--${backgroundColor})`,
        }),
      })}
    >
      {allowsDragging && (
        <Cell>
          <Button slot="drag">≡</Button>
        </Cell>
      )}
      {selectionBehavior === "toggle" && (
        <Cell className={styles.checkboxCell}>
          <TableCheckbox />
        </Cell>
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaRow>
  );
}

const styles = {
  row: {
    outline: "none",
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
  },
  checkboxCell: css({
    paddingLeft: "var(--space-2)",
    paddingRight: "var(--space-1)",
  }),
};
